import { UzButton } from '@utazzo/ui';
import { AlertTriangle, CheckCircle2, KeyRound, Mail } from 'lucide-react';
import React, { useState } from 'react';

import { useSendPasswordReset, useUserEmailLogs } from '../use-users';

import { NOTIFICATION_STATUS_LABEL, humanEnum } from '@/lib/enum-labels';

interface UserEmailLogPanelProps {
  userId: string;
}

const PAGE_SIZE = 25;

const StatusPill = ({ status }: { status: 'PENDING' | 'SENT' | 'FAILED' }): React.JSX.Element => {
  const tone =
    status === 'SENT'
      ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
      : status === 'FAILED'
        ? 'bg-red-50 text-red-700 ring-red-200'
        : 'bg-amber-50 text-amber-700 ring-amber-200';
  const Icon = status === 'SENT' ? CheckCircle2 : status === 'FAILED' ? AlertTriangle : Mail;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium tracking-wider uppercase ring-1 ${tone}`}
    >
      <Icon className="size-3" /> {humanEnum(NOTIFICATION_STATUS_LABEL, status)}
    </span>
  );
};

export const UserEmailLogPanel = ({ userId }: UserEmailLogPanelProps): React.JSX.Element => {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<'PENDING' | 'SENT' | 'FAILED' | ''>('');
  const sendReset = useSendPasswordReset();
  const logs = useUserEmailLogs(userId, {
    page,
    pageSize: PAGE_SIZE,
    sortDir: 'desc',
    ...(status ? { status } : {}),
  });

  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="text-xs tracking-wider text-zinc-500 uppercase">Filter</span>
          {(['', 'SENT', 'PENDING', 'FAILED'] as const).map((s) => (
            <button
              key={s || 'all'}
              type="button"
              onClick={() => {
                setStatus(s);
                setPage(1);
              }}
              className={`rounded-full px-2.5 py-0.5 text-[11px] font-medium tracking-wider uppercase ring-1 transition ${
                status === s
                  ? 'bg-violet-100 text-violet-800 ring-violet-300'
                  : 'bg-white text-zinc-600 ring-zinc-200 hover:bg-zinc-50'
              }`}
            >
              {s || 'all'}
            </button>
          ))}
        </div>
        <UzButton
          variant="ghost"
          size="sm"
          onClick={() => sendReset.mutate(userId)}
          loading={sendReset.isPending}
        >
          <KeyRound className="size-4" /> Send password reset
        </UzButton>
      </div>

      {logs.isLoading ? (
        <div className="p-6 text-sm text-zinc-500">Loading emails...</div>
      ) : logs.error ? (
        <div className="p-6 text-sm text-red-600">Could not load the email log.</div>
      ) : (logs.data?.data ?? []).length === 0 ? (
        <div className="rounded-md border border-dashed border-zinc-200 p-8 text-center text-sm text-zinc-500">
          No emails have been sent to this user yet.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-md border border-zinc-200">
          <table className="min-w-full divide-y divide-zinc-200 text-sm">
            <thead className="bg-zinc-50">
              <tr className="text-left text-[11px] tracking-wider text-zinc-500 uppercase">
                <th className="px-3 py-2">Sent</th>
                <th className="px-3 py-2">Template</th>
                <th className="px-3 py-2">Subject</th>
                <th className="px-3 py-2">To</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Error</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100 bg-white">
              {logs.data!.data.map((row) => (
                <tr key={row.id}>
                  <td className="px-3 py-2 text-zinc-700">
                    {row.sentAt
                      ? new Date(row.sentAt).toLocaleString()
                      : new Date(row.createdAt).toLocaleString()}
                  </td>
                  <td className="px-3 py-2">
                    <div className="text-zinc-900">{row.templateName ?? row.templateCode}</div>
                    <div className="text-[11px] text-zinc-400">{row.templateCode}</div>
                  </td>
                  <td
                    className="max-w-[280px] truncate px-3 py-2 text-zinc-700"
                    title={row.subject}
                  >
                    {row.subject}
                  </td>
                  <td className="px-3 py-2 text-zinc-700">{row.toAddress}</td>
                  <td className="px-3 py-2">
                    <StatusPill status={row.status} />
                  </td>
                  <td
                    className="max-w-[260px] truncate px-3 py-2 text-red-600"
                    title={row.errorMessage ?? ''}
                  >
                    {row.errorMessage ?? '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {logs.data!.pagination.totalPages > 1 && (
            <div className="flex items-center justify-between border-t border-zinc-200 bg-zinc-50 px-3 py-2 text-xs text-zinc-600">
              <span>
                Page {logs.data!.pagination.page} of {logs.data!.pagination.totalPages} ·{' '}
                {logs.data!.pagination.totalItems} total
              </span>
              <div className="flex gap-1">
                <UzButton
                  size="sm"
                  variant="ghost"
                  disabled={!logs.data!.pagination.hasPrev}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </UzButton>
                <UzButton
                  size="sm"
                  variant="ghost"
                  disabled={!logs.data!.pagination.hasNext}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </UzButton>
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
};
